"use client";

import React, { useState, useRef, useEffect } from 'react';
import { useLanguage } from '@/context/LanguageContext';

const languages = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'hi', name: 'हिन्दी', flag: '🇮🇳' },
    { code: 'es', name: 'Español', flag: '🇪🇸' },
    { code: 'fr', name: 'Français', flag: '🇫🇷' },
    { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
    { code: 'pt', name: 'Português', flag: '🇵🇹' },
    { code: 'ru', name: 'Русский', flag: '🇷🇺' },
    { code: 'ja', name: '日本語', flag: '🇯🇵' },
    { code: 'zh', name: '中文', flag: '🇨🇳' },
    { code: 'it', name: 'Italiano', flag: '🇮🇹' },
    { code: 'ko', name: '한국어', flag: '🇰🇷' },
    { code: 'ar', name: 'العربية', flag: '🇸🇦' },
];

const LanguageSwitcher = () => {
    const { language, setLanguage } = useLanguage();
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const currentLang = languages.find(l => l.code === language) || languages[0];

    return (
        <div style={{ position: 'relative' }} ref={dropdownRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.7rem',
                    padding: '0.65rem 1.25rem',
                    borderRadius: '50px',
                    background: '#f8fafc',
                    border: '1px solid #e2e8f0',
                    transition: 'all 0.3s',
                    cursor: 'pointer',
                    minHeight: '44px'
                }}
                onMouseOver={(e) => {
                    e.currentTarget.style.borderColor = 'var(--primary)';
                    e.currentTarget.style.background = '#fff';
                }}
                onMouseOut={(e) => {
                    e.currentTarget.style.borderColor = isOpen ? 'var(--primary)' : '#e2e8f0';
                    e.currentTarget.style.background = '#f8fafc';
                }}
                aria-label="Select Language"
            >
                <span style={{ fontSize: '1.2rem' }}>{currentLang.flag}</span>
                <span className="desktop-only" style={{ fontSize: '0.9rem', fontWeight: 600, color: '#475569' }}>{currentLang.name}</span>
                <span style={{
                    fontSize: '0.7rem',
                    opacity: 0.5,
                    transition: 'transform 0.3s',
                    transform: isOpen ? 'rotate(180deg)' : 'none'
                }}>▼</span>
            </button>

            {isOpen && (
                <div style={{
                    position: 'absolute',
                    right: 0,
                    top: 'calc(100% + 10px)',
                    width: '200px',
                    maxHeight: '400px',
                    overflowY: 'auto',
                    background: '#fff',
                    borderRadius: '16px',
                    border: '1px solid var(--border)',
                    boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)',
                    zIndex: 2000,
                    padding: '0.5rem',
                    animation: 'fadeInUp 0.2s ease-out'
                }}>
                    {languages.map((lang) => (
                        <button
                            key={lang.code}
                            onClick={() => {
                                setLanguage(lang.code as any);
                                setIsOpen(false);
                            }}
                            style={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.85rem',
                                padding: '0.75rem 1rem',
                                borderRadius: '10px',
                                fontSize: '0.9rem',
                                background: language === lang.code ? '#eff6ff' : 'transparent',
                                color: language === lang.code ? 'var(--primary)' : '#475569',
                                fontWeight: language === lang.code ? 700 : 500,
                                transition: 'all 0.2s',
                                cursor: 'pointer',
                                textAlign: 'left'
                            }}
                            onMouseOver={(e) => {
                                if (language !== lang.code) {
                                    e.currentTarget.style.background = '#f8fafc';
                                    e.currentTarget.style.color = 'var(--primary)';
                                }
                            }}
                            onMouseOut={(e) => {
                                if (language !== lang.code) {
                                    e.currentTarget.style.background = 'transparent';
                                    e.currentTarget.style.color = '#475569';
                                }
                            }}
                        >
                            <span style={{ fontSize: '1.25rem' }}>{lang.flag}</span>
                            <span style={{ flex: 1 }}>{lang.name}</span>
                            {language === lang.code && (
                                <span style={{ color: 'var(--primary)', fontSize: '0.8rem' }}>✓</span>
                            )}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

export default LanguageSwitcher;




